import React, { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { CreditCard, Plus, Trash2, RefreshCw, ArrowLeft, Download } from 'lucide-react';
import { doc, serverTimestamp, setDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../utils/firebase';
import { useAuth } from '../../utils/AuthContext';
import { useCRM } from '../../utils/CRMContext';
import { useDebitCardRegistry, useDiscoveredBankAccounts } from '../../hooks/useCardExpenses';
import { backfillCardExpenseAssignments, propagateCardAssignments } from '../../utils/cardExpensesApi';
import { syncSequenceCards } from '../../utils/sequenceApi';
import type { DebitCardAssignment } from '../../types';
import './DebitCardSettingsPanel.css';

const DEBIT_CARDS_DOC = 'financeSettings/debitCards';

function cardToFirestore(c: DebitCardAssignment, uid?: string) {
  const row: Record<string, unknown> = {
    id: c.id,
    lastFour: c.lastFour,
    assignedUserId: c.assignedUserId,
    assignedUserName: c.assignedUserName,
    isActive: c.isActive,
    updatedAt: Timestamp.now(),
  };
  if (uid) row.updatedBy = uid;
  const label = c.cardLabel?.trim();
  if (label) row.cardLabel = label;
  const bankName = c.bankAccountName?.trim();
  if (bankName) row.bankAccountName = bankName;
  const bankId = c.bankAccountId?.trim();
  if (bankId) row.bankAccountId = bankId;
  if (c.sequenceCardId) row.sequenceCardId = c.sequenceCardId;
  if (c.sequencePlatformCardId) row.sequencePlatformCardId = c.sequencePlatformCardId;
  if (c.sequenceCardType) row.sequenceCardType = c.sequenceCardType;
  if (c.sequenceHolderName) row.sequenceHolderName = c.sequenceHolderName;
  if (c.sequencePodId) row.sequencePodId = c.sequencePodId;
  if (c.sequencePodName) row.sequencePodName = c.sequencePodName;
  if (c.sampleMerchants?.length) row.sampleMerchants = c.sampleMerchants;
  if (c.lastFourLocked) row.lastFourLocked = true;
  return row;
}

function cardIsIdentified(card: DebitCardAssignment): boolean {
  return !!(card.sequenceCardId && card.lastFour);
}

function cardIsLinkable(card: DebitCardAssignment): boolean {
  return !!(card.sequencePlatformCardId || card.sequenceCardId);
}

interface DebitCardSettingsPanelProps {
  /** Render inside Settings → Integrations → Sequence (no standalone page chrome). */
  embedded?: boolean;
}

const DebitCardSettingsPanel: React.FC<DebitCardSettingsPanelProps> = ({ embedded = false }) => {
  const { user } = useAuth();
  const { crmUsers } = useCRM();
  const { cards, loading } = useDebitCardRegistry();
  const discovered = useDiscoveredBankAccounts();
  const [saving, setSaving] = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  const [importing, setImporting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [draft, setDraft] = useState<Partial<DebitCardAssignment>>({
    lastFour: '',
    assignedUserId: '',
    cardLabel: '',
    bankAccountName: '',
    isActive: true,
  });

  const employeeOptions = useMemo(
    () =>
      crmUsers
        .filter(u => u.isActive && (u.role === 'employee' || u.role === 'admin' || u.role === 'staff'))
        .sort((a, b) => a.displayName.localeCompare(b.displayName)),
    [crmUsers],
  );

  const persistCards = useCallback(
    async (nextCards: DebitCardAssignment[]) => {
      setSaving(true);
      setMessage(null);
      try {
        const payload: Record<string, unknown> = {
          cards: nextCards.map(c => cardToFirestore(c, user?.id)),
          updatedAt: serverTimestamp(),
        };
        if (user?.id) payload.updatedBy = user.id;
        await setDoc(doc(db, DEBIT_CARDS_DOC), payload, { merge: true });
      } finally {
        setSaving(false);
      }
    },
    [user?.id],
  );

  const runPropagate = useCallback(async (changedCardIds?: string[]) => {
    try {
      const result = await propagateCardAssignments(
        changedCardIds?.length ? { changedCardIds } : undefined,
      );
      const parts: string[] = [];
      if (result.linked > 0) parts.push(`${result.linked} linked to QB`);
      if (result.assigned > 0) parts.push(`${result.assigned} assigned to cardholders`);
      if (parts.length > 0) {
        setMessage(prev => {
          const base = prev ? `${prev} · ` : '';
          return `${base}QB sync: ${parts.join(', ')}.`;
        });
      }
    } catch (err) {
      setMessage(prev => {
        const base = prev ? `${prev} · ` : '';
        return `${base}QB link sync failed: ${err instanceof Error ? err.message : 'Unknown error'}.`;
      });
    }
  }, []);

  const handleAdd = async () => {
    const lastFour = (draft.lastFour || '').replace(/\D/g, '').slice(-4);
    const assignedUserId = draft.assignedUserId || '';
    if (lastFour.length !== 4 || !assignedUserId) {
      setMessage('Last four digits and assigned user are required.');
      return;
    }
    if (cards.some(c => c.isActive && c.lastFour === lastFour && !c.sequenceCardId)) {
      setMessage('An active card with this last-four already exists.');
      return;
    }
    const assignee = crmUsers.find(u => u.uid === assignedUserId);
    const row: DebitCardAssignment = {
      id: crypto.randomUUID(),
      lastFour,
      assignedUserId,
      assignedUserName: assignee?.displayName || 'Unknown',
      isActive: true,
      updatedAt: new Date(),
    };
    const label = draft.cardLabel?.trim();
    if (label) row.cardLabel = label;
    const bankName = draft.bankAccountName?.trim();
    if (bankName) row.bankAccountName = bankName;
    if (user?.id) row.updatedBy = user.id;
    await persistCards([...cards, row]);
    setDraft({ lastFour: '', assignedUserId: '', cardLabel: '', bankAccountName: '', isActive: true });
    setMessage('Card added.');
    await runPropagate([row.id]);
  };

  const assignUser = async (cardId: string, assignedUserId: string) => {
    const assignee = crmUsers.find(u => u.uid === assignedUserId);
    const next = cards.map(c =>
      c.id === cardId
        ? {
            ...c,
            assignedUserId,
            assignedUserName: assignee?.displayName || (assignedUserId ? 'Unknown' : ''),
            updatedAt: new Date(),
          }
        : c,
    );
    await persistCards(next);
    setMessage('Cardholder updated.');
    await runPropagate([cardId]);
  };

  const assignCardIdentity = async (cardId: string, sequenceCardId: string, lastFour: string) => {
    const cardIdClean = sequenceCardId.replace(/\D/g, '').trim();
    const lastFourClean = lastFour.replace(/\D/g, '').slice(-4);
    const next = cards.map(c =>
      c.id === cardId
        ? {
            ...c,
            sequenceCardId: cardIdClean || c.sequenceCardId,
            lastFour: lastFourClean.length === 4 ? lastFourClean : c.lastFour,
            lastFourLocked: lastFourClean.length === 4 ? true : c.lastFourLocked,
            updatedAt: new Date(),
          }
        : c,
    );
    await persistCards(next);
    setMessage('Card identity saved — last 4 will not be overwritten on re-import.');
    await runPropagate([cardId]);
  };

  const toggleActive = async (id: string) => {
    const next = cards.map(c => (c.id === id ? { ...c, isActive: !c.isActive } : c));
    await persistCards(next);
  };

  const removeCard = async (id: string) => {
    if (!window.confirm('Remove this card mapping?')) return;
    await persistCards(cards.filter(c => c.id !== id));
  };

  const runBackfill = async () => {
    setBackfilling(true);
    setMessage(null);
    try {
      const result = await backfillCardExpenseAssignments();
      const parts = [
        `QB rows touched: ${result.updated}`,
        `skipped (already categorized): ${result.skipped}`,
        `${result.cardCount} active card mapping(s)`,
      ];
      if (typeof result.sequenceWritten === 'number') {
        parts.push(`${result.sequenceWritten} Sequence transaction(s) pulled`);
      }
      if (typeof result.sequenceMatched === 'number') {
        parts.push(`${result.sequenceMatched} matched to QB`);
      }
      if (typeof result.sequenceAssigned === 'number') {
        parts.push(`${result.sequenceAssigned} assigned to cardholders`);
      }
      if (result.sequenceError) {
        parts.push(`Sequence sync error: ${result.sequenceError}`);
      }
      setMessage(`Backfill complete — ${parts.join(' · ')}.`);
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Backfill failed.');
    } finally {
      setBackfilling(false);
    }
  };

  const runSequenceImport = async () => {
    setImporting(true);
    setMessage(null);
    try {
      const result = await syncSequenceCards();
      const identified =
        'identifiedCount' in result && typeof result.identifiedCount === 'number'
          ? result.identifiedCount
          : null;
      const needsReview =
        'needsReviewCount' in result && typeof result.needsReviewCount === 'number'
          ? result.needsReviewCount
          : null;
      let detail = `Imported ${result.imported} card(s) — ${result.registryTotal} in registry.`;
      if (identified != null) {
        detail += ` ${identified} fully identified (Card ID + last 4).`;
      }
      if (needsReview != null && needsReview > 0) {
        detail += ` ${needsReview} need review — match by recent charges below, then assign user.`;
      }
      if (typeof result.transactionsWritten === 'number') {
        detail += ` ${result.transactionsWritten} recent charge(s) cached`;
        if (typeof result.matched === 'number') {
          detail += ` · ${result.matched} matched to QuickBooks`;
        }
        if (result.cardExpensesWritten || result.cardExpensesUpdated) {
          detail += ` · ${(result.cardExpensesWritten ?? 0) + (result.cardExpensesUpdated ?? 0)} added to expense list`;
        }
        detail += '.';
      }
      setMessage(detail);
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Sequence import failed.');
    } finally {
      setImporting(false);
    }
  };

  const pickDiscovered = (name: string, lastFour: string | null) => {
    setDraft(d => ({
      ...d,
      bankAccountName: name,
      lastFour: lastFour || d.lastFour || '',
    }));
  };

  const registeredCardsTable = (
    <table className="debit-card-table">
      <thead>
        <tr>
          <th>Card ID</th>
          <th>Last 4</th>
          <th>Holder</th>
          <th>Pod</th>
          <th>Recent charges</th>
          <th>CRM user</th>
          <th>Status</th>
          <th aria-label="Actions" />
        </tr>
      </thead>
      <tbody>
        {cards.map(card => {
          const identified = cardIsIdentified(card);
          const linkable = cardIsLinkable(card);
          return (
            <tr key={card.id} className={!card.isActive ? 'inactive' : !identified ? 'debit-card-needs-review' : ''}>
              <td>
                <input
                  className="debit-card-dashboard-id-input"
                  type="text"
                  inputMode="numeric"
                  placeholder="4857837"
                  defaultValue={card.sequenceCardId || ''}
                  key={`${card.id}-cid-${card.sequenceCardId || ''}`}
                  onBlur={e => assignCardIdentity(card.id, e.target.value, card.lastFour || '')}
                  disabled={saving}
                  title="Card ID from Sequence card drawer"
                />
              </td>
              <td>
                <input
                  className="debit-card-last4-input"
                  type="text"
                  inputMode="numeric"
                  maxLength={4}
                  placeholder="4658"
                  defaultValue={card.lastFour || ''}
                  key={`${card.id}-l4-${card.lastFour || ''}`}
                  onBlur={e => assignCardIdentity(card.id, card.sequenceCardId || '', e.target.value)}
                  disabled={saving}
                  title="Last 4 on physical card (not QuickBooks account name)"
                />
                {card.lastFourLocked && <span className="debit-card-sub"> locked</span>}
              </td>
              <td>{card.sequenceHolderName || card.cardLabel || '—'}</td>
              <td className="debit-card-sub">{card.sequencePodName || '—'}</td>
              <td className="debit-card-sub">
                {card.sampleMerchants?.length
                  ? card.sampleMerchants.slice(0, 3).join(' · ')
                  : identified
                    ? '—'
                    : 'Match by merchant or enter Card ID + last 4'}
              </td>
              <td>
                <select
                  className="debit-card-user-select"
                  value={card.assignedUserId || ''}
                  onChange={e => assignUser(card.id, e.target.value)}
                  disabled={saving || !linkable}
                  title={
                    linkable
                      ? 'Assign cardholder — transactions match via API link'
                      : 'Import from Sequence first'
                  }
                >
                  <option value="">Unassigned</option>
                  {employeeOptions.map(u => (
                    <option key={u.uid} value={u.uid}>
                      {u.displayName}
                    </option>
                  ))}
                </select>
              </td>
              <td>
                <button type="button" className="debit-card-toggle" onClick={() => toggleActive(card.id)} disabled={saving}>
                  {card.isActive ? 'Active' : 'Inactive'}
                </button>
              </td>
              <td>
                <button type="button" className="debit-card-icon-btn" onClick={() => removeCard(card.id)} aria-label="Remove">
                  <Trash2 size={14} />
                </button>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );

  const headerActions = (
    <div className="debit-card-header-actions">
      <button type="button" className="debit-card-btn" onClick={runSequenceImport} disabled={importing || saving}>
        <Download size={14} aria-hidden />
        {importing ? 'Importing…' : 'Import from Sequence'}
      </button>
      <button type="button" className="debit-card-btn" onClick={runBackfill} disabled={backfilling || saving}>
        <RefreshCw size={14} aria-hidden />
        {backfilling ? 'Syncing…' : 'Sync & backfill'}
      </button>
    </div>
  );

  return (
    <div className={`debit-card-settings${embedded ? ' debit-card-settings--embedded' : ''}`}>
      {embedded ? (
        headerActions
      ) : (
        <header className="debit-card-settings-header">
          <div>
            <Link to="/finances/project-pl" className="debit-card-settings-back">
              <ArrowLeft size={14} aria-hidden />
              Project P&amp;L
            </Link>
            <h1>
              <CreditCard size={20} aria-hidden />
              Debit card registry
            </h1>
            <p>
              <strong>1.</strong> Import from Sequence — pulls Card ID, last 4, and holder when a GraphQL session token is configured in Settings → Sequence.
              <br />
              <strong>2.</strong> Verify <strong>Last 4</strong> matches the physical card in Sequence (not the QuickBooks account name). Edit if wrong — it stays locked after you save.
              <br />
              <strong>3.</strong> Assign each card to a CRM user. Transaction sync matches via the hidden API link automatically.
            </p>
          </div>
          {headerActions}
        </header>
      )}

      {message && <p className="debit-card-message">{message}</p>}

      <section className="debit-card-section">
        <h2>Registered cards</h2>
        {loading ? <p>Loading…</p> : cards.length === 0 ? (
          <p className="debit-card-empty">No cards yet — click Import from Sequence.</p>
        ) : (
          registeredCardsTable
        )}
      </section>

      <section className="debit-card-section debit-card-section-muted">
        <h2>Add card manually</h2>
        <p className="debit-card-hint">Fallback when not using Sequence import — matches QB expenses by last 4 on bank account name.</p>
        <div className="debit-card-add-form">
          <label>
            Last 4 digits
            <input
              value={draft.lastFour || ''}
              onChange={e => setDraft(d => ({ ...d, lastFour: e.target.value.replace(/\D/g, '').slice(0, 4) }))}
            />
          </label>
          <label>
            Assigned user
            <select
              value={draft.assignedUserId || ''}
              onChange={e => setDraft(d => ({ ...d, assignedUserId: e.target.value }))}
            >
              <option value="">Select…</option>
              {employeeOptions.map(u => (
                <option key={u.uid} value={u.uid}>
                  {u.displayName}
                </option>
              ))}
            </select>
          </label>
          <label>
            Label (optional)
            <input
              value={draft.cardLabel || ''}
              onChange={e => setDraft(d => ({ ...d, cardLabel: e.target.value }))}
              placeholder="Field crew Amex"
            />
          </label>
          <label>
            QB bank account (optional)
            <input
              value={draft.bankAccountName || ''}
              onChange={e => setDraft(d => ({ ...d, bankAccountName: e.target.value }))}
              placeholder="Project Card (4658)"
            />
          </label>
          <button type="button" className="debit-card-btn" onClick={handleAdd} disabled={saving}>
            <Plus size={14} aria-hidden /> Add card
          </button>
        </div>
      </section>

      {discovered.length > 0 && (
        <section className="debit-card-section debit-card-section-muted">
          <h2>Discovered QuickBooks bank accounts</h2>
          <p className="debit-card-hint">Click to pre-fill manual add only.</p>
          <ul className="debit-card-discovered">
            {discovered.map(row => (
              <li key={row.name}>
                <button type="button" onClick={() => pickDiscovered(row.name, row.lastFour)}>
                  {row.name}{' '}
                  <span className="debit-card-sub">
                    {row.lastFour ? `••${row.lastFour}` : 'no digits'} · {row.count} txn{row.count === 1 ? '' : 's'}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
};

export default DebitCardSettingsPanel;
